import { useState, useEffect } from 'react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import Pagination from '../../../../components/pagination/Pagination'
import { useNavigate } from 'react-router-dom'
import './Debts.scss'

interface DebtBatch {
  _id: string
  userId: string
  userModel: 'User' | 'Business'
  windowStart: string
  windowIndex: number
  totalAmount: number
  amountPaid: number
  remainingAmount: number
  isCleared: boolean
  deadline: string
  hasTriggeredBlock: boolean
  bookingCount: number
  user: {
    _id: string
    name?: string
    org_name?: string
    firstname?: string
    lastname?: string
    email: string
    hasGrace: boolean
  }
  isOverdue: boolean
}

interface DebtBatchesResponse {
  batches: DebtBatch[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

function DebtBatches() {
  const { notify } = useGlobalContext()
  const navigate = useNavigate()
  const [data, setData] = useState<DebtBatchesResponse>({
    batches: [],
    pagination: { total: 0, page: 1, limit: 20, pages: 0 },
  })
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: '',
    userModel: '',
    search: '',
  })

  function fetchBatches(page: number, pageSize: number) {
    setLoading(true)
    let url = `admin/debt/batches?page=${page}&limit=${pageSize}`

    if (filters.status) url += `&status=${filters.status}`
    if (filters.userModel) url += `&userModel=${filters.userModel}`
    if (filters.search) url += `&search=${filters.search}`

    get(url)
      .then((result: any) => {
        if (result.status === 200) {
          setData(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch debt batches')
      })
  }

  useEffect(() => {
    fetchBatches(1, 20)
  }, [])

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSearch = () => {
    fetchBatches(1, 20)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const getUserName = (batch: DebtBatch) => {
    if (batch.userModel === 'Business') {
      return batch.user.org_name || batch.user.name || 'Unknown Business'
    }
    return batch.user.firstname && batch.user.lastname
      ? `${batch.user.firstname} ${batch.user.lastname}`
      : batch.user.name || 'Unknown User'
  }

  const getStatusBadge = (batch: DebtBatch) => {
    if (batch.isCleared) {
      return <span className='badge badge-success'>Cleared</span>
    }
    if (batch.isOverdue) {
      return <span className='badge badge-danger'>Overdue</span>
    }
    return <span className='badge badge-warning'>Pending</span>
  }

  const viewDebtSummary = (userId: string, userModel: string) => {
    navigate(`/admin/debt-summary/${userId}?userModel=${userModel}`)
  }

  return (
    <div className='home'>
      <div className='home_tag flex justify-between'>
        <h2>All Debt Batches</h2>
      </div>

      <div className='home_tables'>
        <div className='left'>
          <div
            className='flex justify-between items-center search-btns flex-wrap'
            style={{ gap: '10px', marginBottom: '20px' }}
          >
            <div className='flex' style={{ gap: '10px' }}>
              <select
                name='status'
                value={filters.status}
                onChange={handleFilterChange}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              >
                <option value=''>All Status</option>
                <option value='overdue'>Overdue</option>
                <option value='pending'>Pending</option>
                <option value='cleared'>Cleared</option>
              </select>

              <select
                name='userModel'
                value={filters.userModel}
                onChange={handleFilterChange}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              >
                <option value=''>All Types</option>
                <option value='User'>Users</option>
                <option value='Business'>Businesses</option>
              </select>
            </div>
            <div className='search flex'>
              <input
                type='search'
                name='search'
                value={filters.search}
                placeholder='Search by name or email'
                onChange={handleFilterChange}
              />
              <button onClick={handleSearch}>Search</button>
            </div>
          </div>

          {loading ? (
            <FormLoader position='center' />
          ) : (
            <>
              {data.batches.length > 0 ? (
                <>
                  <div className='mobile-table'>
                    {data.batches.map((batch: DebtBatch, i: number) => (
                      <div className='card' key={batch._id}>
                        <div className='flex justify-between row'>
                          <div className='count flex items-center justify-center'>
                            {(data.pagination.page - 1) *
                              data.pagination.limit +
                              i +
                              1}
                          </div>
                          {getStatusBadge(batch)}
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Name</h4>
                          <p>{getUserName(batch)}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Type</h4>
                          <p>{batch.userModel}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Total Amount</h4>
                          <p>{formatCurrency(batch.totalAmount)}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Remaining</h4>
                          <p className={batch.isOverdue ? 'failed' : ''}>
                            {formatCurrency(batch.remainingAmount)}
                          </p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Deadline</h4>
                          <p>{formatDate(batch.deadline)}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Bookings</h4>
                          <p>{batch.bookingCount}</p>
                        </div>
                        <div className='row'>
                          <span
                            className='view'
                            onClick={() =>
                              viewDebtSummary(batch.userId, batch.userModel)
                            }
                          >
                            View Summary
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Email</th>
                        <th>Total Amount</th>
                        <th>Paid</th>
                        <th>Remaining</th>
                        <th>Deadline</th>
                        <th>Bookings</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.batches.map((batch: DebtBatch, i: number) => (
                        <tr key={batch._id}>
                          <td>
                            {(data.pagination.page - 1) *
                              data.pagination.limit +
                              i +
                              1}
                          </td>
                          <td>{getUserName(batch)}</td>
                          <td>{batch.userModel}</td>
                          <td>{batch.user.email}</td>
                          <td>{formatCurrency(batch.totalAmount)}</td>
                          <td>{formatCurrency(batch.amountPaid)}</td>
                          <td className={batch.isOverdue ? 'failed' : ''}>
                            {formatCurrency(batch.remainingAmount)}
                          </td>
                          <td>{formatDate(batch.deadline)}</td>
                          <td>{batch.bookingCount}</td>
                          <td>{getStatusBadge(batch)}</td>
                          <td>
                            <span
                              className='view'
                              onClick={() =>
                                viewDebtSummary(batch.userId, batch.userModel)
                              }
                            >
                              view
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <Pagination
                    currentPage={data.pagination.page}
                    totalCount={data.pagination.pages}
                    pageSize={data.pagination.limit}
                    onPageChange={(page: number, pageSize: number) => {
                      fetchBatches(page, pageSize)
                    }}
                  />
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='mdi:cash-multiple' />
                    <h1>No Debt Batches To Show</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default DebtBatches
